import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Component, Inject, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatSidenav } from '@angular/material/sidenav';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SwUpdate } from '@angular/service-worker';
import { Profile } from 'oidc-client';
import { Observable, Subject } from 'rxjs';
import { map, shareReplay, takeUntil } from 'rxjs/operators';
import { AuthService } from 'src/app/core/authentication/auth.service';
import { DoHttpService } from 'src/app/core/services/do-http.service';
import { LayoutService } from 'src/app/core/services/layout.service';
import { NotificationRealtimeService } from 'src/app/core/services/realtime-services/notification-realtime.service';
import { PushNotificationRealtimeService } from 'src/app/core/services/realtime-services/push-notification-realtime.service';
import { UserDetailService } from 'src/app/core/services/user-detail.service';
import { DrawoutAppProService } from 'src/app/modules/drawout-app-pro/services/drawout-app-pro.service';
import { NotificationOpenerIconComponent } from 'src/app/shared/components/layout-related-components/notification-opener-icon/notification-opener-icon.component';
import { ProfileDetailsMenuViewComponent } from 'src/app/shared/components/layout-related-components/profile-details-menu-view/profile-details-menu-view.component';
import { ApiUrls } from 'src/app/shared/constants/ApiUrls';
import { environment } from 'src/environments/environment';
import { NotificationComponent } from '../notification/notification.component';

@Component({
    selector: 'app-common-layout',
    templateUrl: './common-layout.component.html',
    styleUrls: ['./common-layout.component.scss']
})
export class CommonLayoutComponent implements OnInit, OnDestroy {

    @ViewChild(ProfileDetailsMenuViewComponent) profileDetailsComponent: ProfileDetailsMenuViewComponent;
    @ViewChild(NotificationOpenerIconComponent) notificationIconComponent: NotificationOpenerIconComponent;
    @ViewChild(NotificationComponent, { static: false }) notificationContainer: NotificationComponent;
    @ViewChild(MatSidenav) sideNav: MatSidenav;

    notificationOpenend: boolean = false;
    logoFile: string = environment.logo;
    doUser = false;
    isConfigurationAvailable = false;
    isSketchAvailable = false;
    isWorkspaceAvailable = false;

    userProfile: Profile;
    clientVersion = environment.clientVersion;
    version: string;

    $destroy = new Subject<void>();

    isHandset$: Observable<boolean> = this.breakpointObserver.observe(Breakpoints.Handset)
        .pipe(
            map(result => result.matches),
            shareReplay()
        );

    constructor(
        @Inject(SwUpdate) public swUpdate: SwUpdate,
        private breakpointObserver: BreakpointObserver,
        public layoutService: LayoutService,
        private request: DoHttpService,
        private authService: AuthService,
        public notificationRealtimeService: NotificationRealtimeService,
        public pushNotificationRealtimeService: PushNotificationRealtimeService,
        private ngZone: NgZone,
        private snackbar: MatSnackBar,
        private userDetailService: UserDetailService,
        public dproService: DrawoutAppProService) { }

    ngOnInit() {
        // Where initial details are loaded and the nav bar dropdowns are configured
        this.getUserDetails();
        this.request.get(ApiUrls.getVersion).subscribe(res => this.version = res?.version);
        this.ngZone.runOutsideAngular(() => {
            if (this.swUpdate.isEnabled) {
                this.swUpdate.checkForUpdate().then(updateAvailable => {
                    if (updateAvailable) {
                        this.swUpdate.activateUpdate().then(updated => {
                            window.location.reload();
                            // this.snackbar.open("A software update was installed. Please reload to continue.", "Reload").afterDismissed().subscribe(res => {
                            //     window.location.reload();
                            // })
                        });
                    }
                });
            }
        });

        this.layoutService.$toggleSideBarView.pipe(takeUntil(this.$destroy)).subscribe(res => {
            this.toggleSidebarView();
        });
        this.layoutService.$toggleNotification.pipe(takeUntil(this.$destroy)).subscribe(res => {
            if (res) {
                this.openNotification();
            } else {
                this.closeNotification();
            }
        });
    }

    getUserDetails() {
        this.authService.getUser().then(
            res => {
                this.userProfile = res.profile;
                this.notificationRealtimeService.startNotificationServices(res.profile.sub);
                this.pushNotificationRealtimeService.startPushNotificationServices(res.profile.sub);
            }
        );

        const userData = this.userDetailService.getUserData();
        this.doUser = userData.isDORole;
        this.layoutService.drawoutProEnabled.next(userData.isWorkspaceAvailable);
        this.layoutService.zenConfiguratorEnabled.next(userData.isConfigurationAvailable);
        this.layoutService.isSketchAvailable.next(userData.isSketchAvailable);
        this.isConfigurationAvailable = userData.isConfigurationAvailable;
        this.isSketchAvailable = userData.isSketchAvailable;
        this.isWorkspaceAvailable = userData.isWorkspaceAvailable;
    }

    toggleSidebarView() {
        this.sideNav?.toggle();
    }

    closeAllPanel() {
        this.closeAvatar();
        this.closeNotification();
    }

    closeAvatar() {
        this.profileDetailsComponent?.closeAvatar();
    }

    openNotification() {
        this.notificationContainer.showNotifications = !this.notificationContainer.showNotifications;
    }

    closeNotification() {
        this.notificationContainer.showNotifications = false;
        this.notificationIconComponent?.closeNotification();
    }

    toggleSideNavExpandedView() {
        this.layoutService.toggleSideNavExpandedView();
    }

    ngOnDestroy(): void {
        this.$destroy.next();
        this.$destroy.complete();
    }

}
